// Site search: products, pages and blog articles. Pure functions (no React) so they can be tested on their own.
//
// A query matches an item when EVERY word of the query is found somewhere in the item's details
// (name, brand, model, category, description, specs, colours …). Matches at the start of a word rank
// higher than matches in the middle of one, and name matches rank above description matches.

export const MIN_CHARS = 3;

// lower-case, accents removed, punctuation → spaces
export const norm = (s) => String(s ?? '')
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9€.\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export const queryWords = (q) => norm(q).split(' ').filter(Boolean);
export const isSearchable = (q) => norm(q).replace(/\s/g, '').length >= MIN_CHARS;

const prettyCategory = (key) => String(key || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const stripHtml = (s) => String(s ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');

// How well one word matches one piece of text: 0 = no match, 3 = starts a word, 1 = inside a word
function wordScore(word, text) {
  if (!text) return 0;
  if (text.startsWith(word) || text.includes(` ${word}`)) return 3;
  if (word.length >= 3 && text.includes(word)) return 1;
  return 0;
}

// score an item: fields = [{ text, weight }]. Returns 0 when any query word is missing.
function scoreFields(words, fields, nameText) {
  let total = 0;
  for (const w of words) {
    let best = 0;
    for (const f of fields) {
      const s = wordScore(w, f.text) * f.weight;
      if (s > best) best = s;
    }
    if (best === 0) return 0;          // every word must match something
    total += best;
  }
  const whole = words.join(' ');
  if (nameText) {
    if (nameText === whole) total += 40;
    else if (nameText.startsWith(whole)) total += 25;
    else if (nameText.includes(whole)) total += 10;
  }
  return total;
}

// ── products ─────────────────────────────────────────────────────────────────
function productFields(p) {
  const specs = Array.isArray(p.specifications) ? p.specifications.map(s => `${s?.key || ''} ${s?.value || ''}`).join(' ') : '';
  const colors = Array.isArray(p.colors) ? p.colors.map(c => c?.name || '').join(' ') : '';
  const box = Array.isArray(p.boxContents) ? p.boxContents.map(b => (typeof b === 'string' ? b : b?.name || b?.text || '')).join(' ') : '';
  return [
    { text: norm(p.name), weight: 10 },
    { text: norm(p.brand), weight: 7 },
    { text: norm(String(p.model || '').replace(/-/g, ' ')), weight: 6 },
    { text: norm(`${prettyCategory(p.category)} ${prettyCategory(p.subCategory)}`), weight: 5 },
    { text: norm(`${p.range || ''} ${colors}`), weight: 3 },
    { text: norm(`${specs} ${box} ${p.warranty || ''}`), weight: 2 },
    { text: norm(stripHtml(p.description)), weight: 2 },
    { text: norm(`${p.price ?? ''} ${p.oldPrice ?? ''}`), weight: 2 },
  ];
}

export function productMatches(p, query) {
  const words = queryWords(query);
  if (!words.length) return true;
  return scoreFields(words, productFields(p), norm(p.name)) > 0;
}

// words that mean the visitor is looking for parts / accessories rather than a scooter or bike
const PART_WORDS = ['part', 'parts', 'spare', 'spares', 'tyre', 'tyres', 'tire', 'tires', 'tube', 'tubes', 'brake', 'brakes', 'pad', 'pads', 'battery', 'charger', 'helmet', 'lock', 'bag', 'accessory', 'accessories', 'inner', 'outer', 'cable', 'light'];
const isPart = (p) => p.category === 'spare-parts' || p.category === 'accessories';

export function searchProducts(products, query, limit = 6) {
  const words = queryWords(query);
  if (!words.length) return [];
  const wantsParts = words.some(w => PART_WORDS.some(pw => pw.startsWith(w) && w.length >= 3));
  return (products || [])
    .filter(p => p && p.name)
    // "kuk" should list the Kukirin scooters before its inner tube and brake pads (unless parts were asked for)
    .map(p => ({ p, score: scoreFields(words, productFields(p), norm(p.name)) - (isPart(p) && !wantsParts ? 4 : 0) }))
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score || (b.p.inStock !== false) - (a.p.inStock !== false) || Number(a.p.price || 0) - Number(b.p.price || 0))
    .slice(0, limit)
    .map(x => x.p);
}

// ── pages (menu items + key site pages) ──────────────────────────────────────
export const STATIC_PAGES = [
  { title: 'About us', path: '/about', keywords: 'about company story mission values team who we are' },
  { title: 'Contact us', path: '/contact', keywords: 'contact phone call email address location opening hours directions map visit store enquiry message' },
  { title: 'Repairs & servicing', path: '/repair', category: 'Repairs', keywords: 'repair repairs service servicing puncture tyre tire brake battery fix maintenance tune up mechanic' },
  { title: 'Book a repair', path: '/repair-booking', category: 'Repairs', keywords: 'book booking repair appointment drop off pick up' },
  { title: 'Rentals', path: '/rental-booking', keywords: 'rent rental rentals hire bike scooter booking' },
  { title: 'Bike to Work scheme', path: '/bike-to-work', keywords: 'bike to work cycle to work scheme tax salary sacrifice voucher' },
  { title: 'Best sellers', path: '/best-sellers', keywords: 'best sellers popular top selling bestsellers' },
  { title: 'Compare products', path: '/compare', keywords: 'compare comparison side by side' },
  { title: 'Blog & articles', path: '/blog', keywords: 'blog articles news guides tips advice' },
  { title: 'Support centre', path: '/support', category: 'Support', keywords: 'support help centre policies faq' },
  { title: 'Warranty policy', path: '/support/warranty', category: 'Support', keywords: 'warranty guarantee covered faulty defect' },
  { title: 'Return & refund policy', path: '/support/return-refund', category: 'Support', keywords: 'return returns refund exchange cancel money back' },
  { title: 'Terms of service', path: '/support/tos', category: 'Support', keywords: 'terms conditions tos legal' },
  { title: 'Privacy policy', path: '/support/privacy', category: 'Support', keywords: 'privacy data gdpr personal information cookies' },
  { title: 'Delivery & Klarna', path: '/products', keywords: 'delivery shipping klarna finance instalments pay later payment' },
];

// flatten the admin-managed menu (top items, sub items, nested children) into searchable pages
export function pagesFromMenu(menu) {
  const out = [];
  // `category` = the menu section an item sits under (shown under its title in the search results)
  (menu || []).forEach(m => {
    if (m?.name && m.path) out.push({ title: m.name, path: m.path, keywords: '', category: '' });
    (m?.submenu || []).forEach(s => {
      if (s?.name && s.path) out.push({ title: s.name, path: s.path, keywords: m.name || '', category: m.name || '' });
      (s?.children || []).forEach(c => { if (c?.name && c.path) out.push({ title: c.name, path: c.path, keywords: `${m.name || ''} ${s.name || ''}`, category: m.name || '' }); });
    });
  });
  return out;
}

export function searchPages(pages, query, limit = 4) {
  const words = queryWords(query);
  if (!words.length) return [];
  const seen = new Set();
  return (pages || [])
    .map(pg => ({ pg, score: scoreFields(words, [{ text: norm(pg.title), weight: 10 }, { text: norm(pg.keywords), weight: 4 }], norm(pg.title)) }))
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .filter(x => { const k = x.pg.path; if (seen.has(k)) return false; seen.add(k); return true; })   // one entry per address
    .slice(0, limit)
    .map(x => x.pg);
}

// ── blog articles ────────────────────────────────────────────────────────────
export function searchPosts(posts, query, limit = 3) {
  const words = queryWords(query);
  if (!words.length) return [];
  return (posts || [])
    .filter(p => p && p.title && p.slug)
    .map(p => ({
      p,
      score: scoreFields(words, [
        { text: norm(p.title), weight: 10 },
        { text: norm(`${p.category || ''} ${p.author || ''}`), weight: 4 },
        { text: norm(stripHtml(p.excerpt)), weight: 3 },
        { text: norm(stripHtml(p.content).slice(0, 1500)), weight: 1 },
      ], norm(p.title)),
    }))
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(x => x.p);
}

// ── everything at once ───────────────────────────────────────────────────────
export function searchAll(data, query) {
  if (!isSearchable(query)) return { products: [], pages: [], posts: [], total: 0 };
  const products = searchProducts(data.products, query, 6);
  const pages = searchPages([...pagesFromMenu(data.menu), ...STATIC_PAGES], query, 4);
  const posts = searchPosts(data.posts, query, 3);
  return { products, pages, posts, total: products.length + pages.length + posts.length };
}

// split text into [{ text, hit }] pieces so the matched letters can be shown in bold
export function highlight(text, query) {
  const t = String(text ?? '');
  const words = [...new Set(queryWords(query))].filter(w => w.length >= 1).sort((a, b) => b.length - a.length);
  if (!t || !words.length) return [{ text: t, hit: false }];
  const re = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return t.split(re).filter(Boolean).map(part => ({ text: part, hit: words.some(w => part.toLowerCase() === w) }));
}
